import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { TimeEntryPhaseT } from '@toga/shared'

/**
 * #8 — Timer integrado de TimeEntry.
 *
 * Estado mínimo del timer activo, compartido por toda la app. Persistido en
 * localStorage para sobrevivir a un refresh: si el usuario recarga con un
 * timer en marcha, el pill flotante reaparece con el contador correcto.
 *
 * Solo puede haber un timer activo (entre oportunidades). Si el usuario
 * intenta iniciar otro, el `start` reemplaza el anterior.
 */
export interface ActiveTimer {
  opportunityId: string
  opportunityCode: string
  /** ISO string del instante en que arrancó el cronómetro. */
  startedAt: string
  phase: TimeEntryPhaseT
}

interface TimerContextValue {
  active: ActiveTimer | null
  /** Inicia un nuevo timer para esta oportunidad. */
  start: (input: { opportunityId: string; opportunityCode: string; phase?: TimeEntryPhaseT }) => void
  /** Detiene el timer activo y devuelve los metadatos para crear el TimeEntry. */
  stop: () => ActiveTimer | null
  /** Cancela el timer sin guardar (descarta). */
  cancel: () => void
  /** Devuelve `true` si hay un timer activo para esta oportunidad concreta. */
  isActiveFor: (opportunityId: string) => boolean
}

const STORAGE_KEY = 'jr2.timer.active'

const TimerContext = createContext<TimerContextValue | null>(null)

export function useTimer(): TimerContextValue {
  const ctx = useContext(TimerContext)
  if (!ctx) throw new Error('useTimer must be used within <TimerProvider>')
  return ctx
}

function loadFromStorage(): ActiveTimer | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ActiveTimer
    if (
      !parsed ||
      typeof parsed.opportunityId !== 'string' ||
      typeof parsed.opportunityCode !== 'string' ||
      typeof parsed.startedAt !== 'string' ||
      (parsed.phase !== 'PREVENTA' && parsed.phase !== 'EJECUCION')
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function saveToStorage(value: ActiveTimer | null): void {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignoramos errores de quota/disabled storage
  }
}

export function TimerProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveTimer | null>(() => loadFromStorage())

  // Persistimos cada cambio en localStorage.
  useEffect(() => {
    saveToStorage(active)
  }, [active])

  // Sincronización entre pestañas: si en otra pestaña paran/inician el timer,
  // esta pestaña lo refleja en tiempo real.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return
      setActive(loadFromStorage())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const start = useCallback<TimerContextValue['start']>(
    ({ opportunityId, opportunityCode, phase = 'EJECUCION' }) => {
      setActive({
        opportunityId,
        opportunityCode,
        startedAt: new Date().toISOString(),
        phase,
      })
    },
    [],
  )

  const stop = useCallback<TimerContextValue['stop']>(() => {
    let snapshot: ActiveTimer | null = null
    setActive((prev) => {
      snapshot = prev
      return null
    })
    return snapshot
  }, [])

  const cancel = useCallback(() => {
    setActive(null)
  }, [])

  const isActiveFor = useCallback(
    (opportunityId: string) => active?.opportunityId === opportunityId,
    [active],
  )

  const value = useMemo<TimerContextValue>(
    () => ({ active, start, stop, cancel, isActiveFor }),
    [active, start, stop, cancel, isActiveFor],
  )

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>
}
